import { normalizedConversationPhone } from "../conversationIdentity.mjs";

const localDateKey = value => (
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
);

export function vendasMonthRange(now = new Date(), monthIndex = 0) {
  const start = new Date(now.getFullYear(), now.getMonth() - monthIndex, 1);
  return {
    from:start,
    to:monthIndex === 0
      ? new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
      : new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999),
  };
}

export function vendasMonthLabels(now = new Date()) {
  return Array.from({ length:6 }, (_, index) => new Date(
    now.getFullYear(), now.getMonth() - index, 1,
  ).toLocaleDateString("pt-BR", { month:"long", year:"numeric" }));
}

export function vendasBreakdownForUser({
  clients, leads = [], prospeccao = [], currentUser, periodRange, liftSales, calculate, redact,
}) {
  if (currentUser.role === "consultant") {
    const breakdown = calculate({
      clients,
      leads,
      prospeccao,
      periodRange,
      storeId:null,
      consultantId:currentUser.id,
      consultantAcrossStores:true,
      liftSales,
    });
    return redact(breakdown, currentUser.storeId);
  }
  return calculate({
    clients,
    leads,
    prospeccao,
    periodRange,
    storeId:currentUser.role === "manager" ? currentUser.storeId : null,
    consultantId:null,
    liftSales,
  });
}

export const OUTRA_LOJA_ORIGEM_NAO_EXIBIDA = "Venda em outra loja · origem não exibida";

export function buildVendasOriginTotals(sales) {
  const totals = new Map();
  for (const sale of sales || []) {
    const origem = sale?.crossStore
      ? OUTRA_LOJA_ORIGEM_NAO_EXIBIDA
      : sale?.origin || "Outros";
    const current = totals.get(origem) || { origem, valor:0, qtd:0 };
    current.valor += Number(sale?.value) || 0;
    current.qtd += 1;
    totals.set(origem, current);
  }
  return [...totals.values()].sort((a, b) => b.valor - a.valor);
}

export function saleConversationRoute(sale, storeIdFallback = null) {
  if (!sale || sale.crossStore) return null;
  const client = sale.client;
  const record = sale.record;
  const recordType = String(sale.recordType || "").toLowerCase();
  const linkedType = String(client?.financialSourceType || "").toLowerCase();
  const linkedLiftType = recordType === "lift"
    && ["lead", "prospeccao", "client"].includes(String(sale.linkedSourceType || "").toLowerCase())
    ? String(sale.linkedSourceType).toLowerCase()
    : null;
  if (recordType === "lift" && sale.unresolvedSourceLink) return null;
  const linkedClientSourceId = linkedType === "lead"
    ? client?.financialSourceId || client?.leadId || client?.lead_id
    : linkedType === "prospeccao"
      ? client?.financialSourceId || client?.prospId || client?.prosp_id || client?.prospeccaoId || client?.prospeccao_id
      : null;
  const linkedClientSourceType = linkedClientSourceId != null && linkedClientSourceId !== ""
    && ["lead", "prospeccao"].includes(linkedType) ? linkedType : null;
  const sourceType = ["lead", "prospeccao"].includes(recordType)
    ? recordType
    : linkedLiftType && linkedLiftType !== "client"
      ? linkedLiftType
      : linkedClientSourceType || (linkedLiftType === "client" ? "client" : recordType || (client ? "client" : ""));
  const moduleId = sourceType === "lead" ? "leads"
    : sourceType === "prospeccao" ? "prospeccao"
      : ["client", "kanban"].includes(sourceType) ? "kanban"
        : null;
  const sourceRecordId = sourceType === "lead"
    ? recordType === "lead" ? record?.id
      : linkedLiftType === "lead" ? sale.linkedSourceId : linkedClientSourceId
    : sourceType === "prospeccao"
      ? recordType === "prospeccao" ? record?.id
        : linkedLiftType === "prospeccao" ? sale.linkedSourceId : linkedClientSourceId
      : moduleId === "kanban"
        ? linkedLiftType === "client" ? sale.linkedSourceId : (client?.id ?? record?.id)
        : null;
  if (["lead", "prospeccao"].includes(recordType)
    && (sourceRecordId == null || sourceRecordId === "")) return null;
  if (linkedLiftType && (sourceRecordId == null || sourceRecordId === "")) return null;
  const isDirectLiftSource = linkedLiftType === "lead" || linkedLiftType === "prospeccao";
  const identityRecord = isDirectLiftSource ? null
    : sourceType === "lead" || sourceType === "prospeccao"
      ? recordType === sourceType ? record : client
      : client || record;
  const phone = isDirectLiftSource
    ? sale.linkedSourcePhone || ""
    : identityRecord?.telefone || identityRecord?.phone || sale.phone || record?.telefone || record?.phone || "";
  if (!phone) return null;
  const storeId = isDirectLiftSource
    ? sale.linkedSourceStoreId
    : sourceType === "lead"
      ? identityRecord?.lojaAtual ?? identityRecord?.storeId ?? sale.storeId ?? storeIdFallback
      : identityRecord?.lojaAtual ?? identityRecord?.storeId ?? sale.storeId ?? record?.storeId ?? storeIdFallback;
  if (storeId == null) return null;
  const clientFallbackStoreId = client?.lojaAtual ?? client?.storeId ?? sale.storeId ?? storeIdFallback;
  const clientFallbackPhone = client?.telefone || client?.phone || "";
  const clientFallback = ["lead", "prospeccao"].includes(sourceType)
    && client?.id != null && clientFallbackStoreId != null && clientFallbackPhone
    ? {
      moduleId:"kanban",
      storeId:clientFallbackStoreId,
      phone:clientFallbackPhone,
      recordId:String(client.id),
    }
    : null;
  // An unlinked LIFT record has no CRM record identity. The caller may safely
  // fall back to the existing store-scoped oldest-source resolver.
  if (!moduleId && recordType === "lift") {
    return { moduleId:null, storeId, phone, fallback:true };
  }
  if (!moduleId) return null;
  return {
    moduleId,
    storeId,
    phone,
    recordId:sourceRecordId == null || sourceRecordId === "" ? null : String(sourceRecordId),
    ...(clientFallback ? { clientFallback } : {}),
    channelStoreId:moduleId === "leads"
      ? Number(isDirectLiftSource
        ? sale.linkedSourceChannelStoreId
        : identityRecord?.whatsappStoreId ?? identityRecord?.lojaOrigem ?? identityRecord?.storeId) || Number(storeId)
      : undefined,
  };
}

function recordResponsibleStore(record, moduleId) {
  return Number(moduleId === "leads" ? record?.lojaAtual ?? record?.storeId : record?.storeId);
}

function recordPhone(record, moduleId) {
  return moduleId === "leads" ? record?.telefone || record?.phone
    : record?.phone || record?.telefone;
}

function recordConsultantId(record) {
  return record?.consultantId ?? record?.consultorId ?? record?.assignedConsultantId
    ?? record?.consultorAtual ?? record?.consultor_atribuido;
}

function recordCreatedAt(record) {
  const value = record?.createdAt || record?.data_criacao || record?.criadoEm || record?.date;
  const timestamp = typeof value === "number" ? value : Date.parse(value || "");
  return Number.isFinite(timestamp) ? timestamp : Infinity;
}

export function resolveVendasSourceRecord(route, {
  clients = [], leads = [], prospeccao = [], currentUser = {}, resolveLeadConsultantId = null,
} = {}) {
  if (!route || !["kanban", "leads", "prospeccao"].includes(route.moduleId)) return null;
  const storeId = Number(route.storeId);
  if (!Number.isFinite(storeId) || storeId <= 0 || !route.phone) return null;
  if (["consultant", "manager"].includes(currentUser.role)
    && Number(currentUser.storeId) !== storeId) return null;
  const records = route.moduleId === "leads" ? leads
    : route.moduleId === "prospeccao" ? prospeccao : clients;
  const matching = (records || []).filter(record => (
    recordResponsibleStore(record, route.moduleId) === storeId
    && normalizedConversationPhone(recordPhone(record, route.moduleId)) === normalizedConversationPhone(route.phone)
    && (route.recordId == null || String(record?.id || "") === String(route.recordId))
    && (currentUser.role !== "consultant"
      || Number(route.moduleId === "leads" && resolveLeadConsultantId
        ? resolveLeadConsultantId(record)
        : recordConsultantId(record)) === Number(currentUser.id))
  ));
  if (route.recordId != null) return matching[0] || null;
  return matching.sort((a, b) => recordCreatedAt(a) - recordCreatedAt(b))[0] || null;
}

export function resolveVendasConversation(route, options = {}) {
  const record = resolveVendasSourceRecord(route, options);
  if (record) return { route, record };
  if (!route?.clientFallback) return null;
  const fallbackRecord = resolveVendasSourceRecord(route.clientFallback, options);
  return fallbackRecord ? { route:route.clientFallback, record:fallbackRecord } : null;
}

export function sortVendasByDate(sales) {
  return [...(sales || [])].sort((a, b) => {
    const dateA = Date.parse(a?.saleDate || "") || 0;
    const dateB = Date.parse(b?.saleDate || "") || 0;
    return dateB - dateA || String(b?.id || "").localeCompare(String(a?.id || ""));
  });
}

export function buildVendasMonthRows(sales, {
  currentUser, users = [], stores = [], onOpenConversation = null, onOpenSale = null,
}) {
  return sortVendasByDate(sales).map(sale => {
    const crossStore = sale.crossStore === true;
    const route = saleConversationRoute(sale, currentUser.storeId);
    const name = crossStore ? "Venda em outra loja" : sale.name || "Venda";
    return {
      id:sale.id,
      nome:name,
      valor:sale.value,
      data:crossStore ? "" : sale.saleDate,
      origem:crossStore ? "" : sale.origin,
      consultor:crossStore ? "" : users.find(user => Number(user.id) === Number(sale.consultantId))?.name?.split(" ")[0] || "",
      loja:currentUser.role === "admin" || currentUser.role === "superadmin"
        ? stores.find(store => Number(store.id) === Number(sale.storeId))?.name || "" : "",
      abrir:() => {
        if (onOpenSale) return onOpenSale(sale);
        if (!crossStore && route && onOpenConversation) return onOpenConversation(route);
        return undefined;
      },
    };
  });
}

export function acompanhamentoScope(client, currentUser) {
  if (currentUser.role === "admin" || currentUser.role === "superadmin") return true;
  if (Number(client?.storeId) !== Number(currentUser.storeId)) return false;
  return currentUser.role !== "consultant"
    || Number(client?.consultantId) === Number(currentUser.id);
}

export function buildVendasFollowUps(clients, {
  currentUser, users = [], wasSold, now = new Date(), onOpenConversation = null,
  onOpenFollowUp = null,
}) {
  const today = localDateKey(now);
  return (clients || []).filter(client => wasSold(client) && acompanhamentoScope(client, currentUser))
    .map(client => {
      const column = client.stage === "nextSale" ? "proxima"
        : client.stage === "posVenda" ? "posvenda"
          : client.deliveryStatus === "entregue" ? "entregue" : "aguardando";
      const date = column === "aguardando" ? client.deliveryDate
        : column === "entregue" ? client.posVendaDate
          : column === "posvenda" ? client.posVendaDate || client.nextSaleDate
            : client.nextSaleDate;
      const actionLabel = column === "aguardando" ? "Entrega prevista"
        : column === "entregue" ? "Pós-venda em"
          : column === "posvenda" ? "Ligar em" : "Ofertar de novo em";
      const dateKey = date ? String(date).slice(0, 10) : "";
      const route = saleConversationRoute({
        client,
        storeId:client.storeId,
        phone:client.phone,
      }, currentUser.storeId);
      return {
        id:client.id,
        nome:client.name,
        valor:client.value,
        produto:client.product,
        origem:client.origin,
        consultor:users.find(user => Number(user.id) === Number(client.consultantId))?.name?.split(" ")[0] || "",
        coluna:column,
        proximo:dateKey ? `${actionLabel} ${dateKey.split("-").reverse().join("/")}`
          : column === "aguardando" ? "Sem data de entrega" : null,
        atrasado:Boolean(dateKey && dateKey < today),
        abrir:() => {
          if (onOpenFollowUp) return onOpenFollowUp(client);
          if (route && onOpenConversation) return onOpenConversation(route);
          return undefined;
        },
      };
    });
}